import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { fetchSpot, updateSpot, deleteSpot, setLastSpotRoute } from '../../store/slices/savedSpotsSlice';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import PageNavSavedLocation from '../../layout/PageNavSavedLocation';
import Input from '../../components/fields/Input';
import ButtonLightBlue from '../../components/buttons/ButtonLightBlue';
import Dialog from '../../components/common/Dialog';
import SuccessMessage from '../../components/messages/SuccessMessage';
import ErrorMessage from '../../components/messages/ErrorMessage';
import ButtonRedInverted from '../../components/buttons/ButtonRedInverted';
import ButtonLinkLightBlue from '../../components/buttons/ButtonLinkLightBlue'
import {Helmet} from "react-helmet";

const SpotEdit = () => {

    const { id } = useParams();
    const location = useLocation();
    const { spots, errors, isDeleting, isUpdating } = useSelector(state => state.savedSpots);
    const currentSpot = id ? spots[id] : null;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [deleteMessage, setDeleteMessage] = React.useState(null);
    const [showDialog, setShowDialog] = React.useState(false);
    
    const [formData, setFormData] = useState({
        name: currentSpot?.name || ''
    });

    React.useEffect(() => {
        dispatch(fetchSpot(id));
    }, [dispatch, id]);

    React.useEffect(() => {
        if (id) dispatch(setLastSpotRoute({ spotId: id, path: location.pathname }));
    }, [dispatch, id, location.pathname]);

    React.useEffect(() => {
        if (currentSpot) {
            setFormData({
                name: currentSpot.name || ''
            });
        }
    }, [currentSpot]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDelete = async () => {
        try {
            const spotName = currentSpot.name;
            await dispatch(deleteSpot(currentSpot.id)).unwrap();
            setDeleteMessage(`${spotName} has been archived`);
            setShowDialog(false);
        } catch (error) {
            setDeleteMessage('Failed to archive spot');
            setShowDialog(false);
        }
    };

    const handleOnSubmit = async (e) => {
        e.preventDefault();
        
        const spotData = {
            name: formData.name
        };

        try {

            await dispatch(updateSpot({spotId: id, spotData})).unwrap();

            navigate('/location/' + id + '/view');

        } catch (error) {
            // Error is available via redux state.savedSpots.errors
        }
    };

    if(!currentSpot && !deleteMessage){
       return <p>Fetching Data...</p>
    }

    return (
        <>
            <Helmet>
                <title>Edit Spot | Oreca Pro</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            <MobileNav />

            <Dialog
                isOpen={showDialog}
                onClose={() => setShowDialog(false)}
                onConfirm={handleDelete}
                title="Archive Spot"
                confirmText="Archive Spot"
                cancelText="Cancel"
                isLoading={isDeleting}
            >
                <p className="text-custom-light-grey">Are you sure you want to archive "{currentSpot?.name}"? You will be able to restore it later.</p>
            </Dialog>

            <div className='pt-[76px] pb-[90px] px-4 max-w-[1100px] mx-auto'>
                {deleteMessage ? (
                    <div className="max-w-[400px] mx-auto mt-10">
                        {deleteMessage.includes('Failed') ? (
                            <ErrorMessage message={deleteMessage} />
                        ) : (
                            <SuccessMessage message={deleteMessage} />
                        )}
                        <div className="mt-6">
                            <ButtonLinkLightBlue 
                                href="/"
                                text="Back to Map"
                            />
                        </div>
                    </div>
                ) : (
                    <>
                        <h1 className='text-2xl font-semibold mb-1 mt-2'>{currentSpot.name ? currentSpot.name : "Current Spot"}</h1>
                        <p className='text-xs text-custom-light-grey'>
                            {Number(currentSpot.latitude).toFixed(2)}, {Number(currentSpot.longitude).toFixed(2)}
                        </p>

                        <PageNavSavedLocation id={currentSpot.id} />

                        <form onSubmit={handleOnSubmit} className='max-w-[400px] mx-auto mt-10'>
                            {errors && (
                                <div style={{ color: 'red', marginBottom: '10px' }}>
                                    {Object.values(errors).map((error, index) => (
                                        <div key={index}>{error}</div>
                                    ))}
                                </div>
                            )}

                            <h2 className="text-xl font-semibold mb-6 text-white">Edit</h2>

                            <Input 
                                type="text" 
                                name="name" 
                                value={formData.name}
                                placeholder="Enter Spot Name"
                                onChange={handleInputChange}
                                required
                            />
                            <div className='mt-8 flex flex-col gap-4'>
                                <ButtonLightBlue 
                                    type="submit" 
                                    text={isUpdating ? 'Updating...' : 'Update'} 
                                />
                            </div>

                            <h2 className="text-xl font-semibold mb-6 mt-12 text-white">Actions</h2>

                            <div className="mt-4 mb-12 flex flex-col gap-4">
                                <ButtonRedInverted 
                                    type="button"
                                    onClick={() => setShowDialog(true)}
                                    text="Archive Spot"
                                />
                            </div>
                        </form>
                    </>
                )}

                
            </div>
            
        </>
    )
};

export default SpotEdit;
